import {
	createFileRoute,
	Outlet,
	redirect,
	useNavigate,
	useRouteContext,
	useRouter,
	useSearch,
} from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { commitLocalUpdate, useRelayEnvironment } from "react-relay";
import { Icon } from "@/components/icon";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { logout as logoutHandler } from "@/handlers/logout";
import { CreatorTopMenu } from "@/ui/creator-top-menu";
import { getRequestUrl } from "@/utils/getRequestUrl";

export const Route = createFileRoute("/_visibility")({
	component: RouteComponent,
	loader: async ({ context }) => {
		if (context.club.visibility !== "PUBLIC" && !context.session) {
			const requestUrl = await getRequestUrl();
			const url = new URL(requestUrl);

			throw redirect({
				to: "/login",
				search: {
					redirectTo: url.pathname,
				},
			});
		}
	},
});

function RouteComponent() {
	const { club, session } = useRouteContext({ from: "__root__" });
	const { preview: isPreview } = useSearch({ from: "__root__" });

	const environment = useRelayEnvironment();
	const logout = useServerFn(logoutHandler);

	const router = useRouter();
	const navigate = useNavigate({ from: Route.fullPath });

	if (club.visibility !== "PUBLIC" && !club.isMember) {
		return (
			<div className="flex flex-col gap-3 w-full h-dvh">
				<div className="flex items-center justify-between border-b border-sidebar-border p-6">
					<div className="flex flex-row items-center justify-start mr-3 gap-1.5">
						{club?.logo ? (
							<Avatar>
								<AvatarImage
									src={club.logo?.signedUrl}
									alt={`${club.name} logo`}
								/>
								<AvatarFallback>
									{club.name.slice(0, 2).toUpperCase()}
								</AvatarFallback>
							</Avatar>
						) : null}

						<span className="line-clamp-1">{club?.name}</span>
					</div>

					<DropdownMenu>
						<DropdownMenuTrigger>
							<Avatar>
								{session?.user?.photo?.signedUrl ? (
									<AvatarImage src={session?.user?.photo?.signedUrl} />
								) : null}
								<AvatarFallback>
									{session?.user?.avatarFallback || "??"}
								</AvatarFallback>
							</Avatar>
						</DropdownMenuTrigger>

						<DropdownMenuContent>
							<DropdownMenuItem
								onClick={() => {
									if (confirm("Tem certeza que deseja fazer logout?")) {
										logout().then(async () => {
											commitLocalUpdate(environment, (store) =>
												store.invalidateStore(),
											);

											router.invalidate();

											navigate({
												reloadDocument: true,
											});
										});
									}
								}}
							>
								<Icon data-slot="icon" name="LogOut" />
								Fazer Logout
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
				<div className="flex-1 flex items-center justify-center gap-3 flex-col">
					<h1 className="font-semibold">Acesso Negado</h1>

					<span className="text-balance max-w-2xl text-center">
						Sua conta não tem permissão para acessar este conteúdo. Entre em
						contato com o administrador para mais informações
					</span>
				</div>
			</div>
		);
	}

	return (
		<div className="md:flex md:flex-col md:max-h-100dvh lg:overflow-y-hidden">
			{!isPreview && <CreatorTopMenu />}

			<Outlet />
		</div>
	);
}
